import { formatDateTimeForGraphLabel } from "@src/cloud-connect/utils/format-date-time-for-graph-label";
import { warningSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { getMachineStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-machine-structure-nodeset";
import { preconfiguredColors } from "./graph-color-palette";
import { searchUnitsList } from "../unit-input-and-popover/search-units-list";
import { ToolboxBuilder } from "@src/cloud-connect/charts/toolbox-builder";
import { GridBuilder } from "@src/cloud-connect/charts/grid-builder";
import { XAxisBuilder } from "@src/cloud-connect/charts/xaxis-builder";
import store from "@src/reducers/store";
import { i18n } from "@src/i18n/i18n";
import {
  buildAncestorBreadCrumbs,
  tooltipFormatter,
  legendFormatter,
} from "@src/cloud-connect/utils/graph-formatters";

// Function to build the ECharts option object dynamically
export const buildAggregatedDataGraphOptionObject = (
  tileTitle,
  arrayOfNodes,
  nodes,
) => {
  const language = store.getState().yourSlice.language;
  const texts = {
    warningMessage: i18n("modules.dashboardPanel.warningMessage", language),
  };
  if (!arrayOfNodes) return null;

  const arrayOfComputedUnits = [];

  // Step 1: Filter arrayOfNodes to include only those with status 200
  const validNodes = arrayOfNodes.filter((node) => {
    if (node.status === 200 && node.body.meta_data) {
      if (
        node.body.meta_data.timeseries_type === "computed" &&
        node.body.meta_data.unit
      ) {
        arrayOfComputedUnits.push(
          searchUnitsList(node.body.meta_data.unit.toString())[0].display_name,
        );
      }
      return node;
    }
  });

  if (!validNodes.length) return null;

  // Step 2: Extract the times array from the first valid node's aggregated values
  let timesArray = [];
  for (let i = 0; i < validNodes.length; i++) {
    if (validNodes[i]?.body?.aggregated_values) {
      timesArray = validNodes[i].body.aggregated_values.map((av) => av.time);
      break; // Exit the loop once a valid time array is found
    }
  }

  // notify the user if some nodes were excluded
  if (validNodes.length !== arrayOfNodes.length) {
    warningSnackbar(texts.warningMessage, 10000);
  }

  // Step 3: Build the series array dynamically based on the provided nodes
  const seriesArray = nodes.map((node, index) => {
    // Find the corresponding valid node by matching the source with the node id
    const correspondingNode = validNodes.find(
      (item) => item.body.meta_data.source === node.id,
    );

    // Get the number of timestamps or data points from the first valid series
    const dataLength = timesArray.length;

    const data = correspondingNode
      ? correspondingNode.body.aggregated_values.map((av) => ({
          value: av.v_avg,
          location: buildAncestorBreadCrumbs(node.id),
        }))
      : Array(dataLength).fill({
          value: null,
          location: buildAncestorBreadCrumbs(node.id),
        });

    const uniqueIdentifier = `${node.name} (${index + 1})`;

    // Return the series object for this node
    return {
      animation: true,
      animationDuration: 1000,
      animationEasing: "quadraticIn",
      animationDelay: 0,
      data: data,
      itemStyle: {
        color: preconfiguredColors[index % preconfiguredColors.length], // Use color for symbols
      },
      lineStyle: {
        width: 2,
        color: preconfiguredColors[index % preconfiguredColors.length],
      },
      name: uniqueIdentifier,
      showSymbol: data.length === 1 ? true : false,
      smooth: 0.4,
      symbol: "circle",
      symbolSize: 8,
      type: node.chart_type || "line", // Use node's chart_type or default to 'line'
      connectNulls: true,
    };
  });

  // Step 4: Use an array to collect all the units
  let units;

  if (arrayOfComputedUnits.length > 0) {
    units = arrayOfComputedUnits;
  } else {
    units = nodes.map((node) => {
      return getMachineStructureNodeset().getNodeById(node.id)?.attributes?.unit
        ?.display_name; // Retrieve display_name or null
    });
  }

  // Step 5: Determine Y-axis name
  const firstUnit = units[0]; // Take the first unit as reference
  const allSameUnit = units.every(
    (unit) => unit === firstUnit && unit !== null,
  ); // Check if all are the same and not null

  const yAxisName = allSameUnit && firstUnit ? firstUnit : "Unit"; // Use the display name if all are the same, otherwise "Unit"
  // Step 6: Build and return the final graph options object
  const graphOptions = {
    color: preconfiguredColors,
    grid: new GridBuilder().build(),
    toolbox: new ToolboxBuilder()
      .withDataView()
      .withSaveAsImage({ name: tileTitle })
      .build(),
    xAxis: new XAxisBuilder()
      .setData(timesArray)
      .setFormatter(formatDateTimeForGraphLabel)
      .build(),
    yAxis: {
      name: yAxisName, // Use the determined name here
      type: "value",
      scale: true,
      axisLine: {
        onZero: true,
      },
      nameTextStyle: {
        padding: [-11, 0, 0, -20],
        verticalAlign: "top",
        align: "left",
      },
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(255, 255, 255, 0.3)", // Semi-transparent background
      confine: true,
      borderColor: "gray",
      borderWidth: 1,
      textStyle: {
        fontFamily: "Inter", // Custom font family (or replace with preferred font)
      },
      formatter: tooltipFormatter,
      extraCssText:
        "backdrop-filter: blur(5px); padding: 15px 20px 10px; box-sizing: border-box;",
    }, // Add box-sizing to include padding    },
    series: seriesArray, // Dynamic series built from valid nodes and provided nodes
    legend: {
      show: true,
      orient: "horizontal",
      left: "center",
      bottom: 0,
      align: "auto",
      padding: 5,
      itemGap: 10,
      textStyle: {
        color: "#e6f1f2",
      },
      formatter: legendFormatter,
    },
  };

  return graphOptions;
};
